import { CheckIcon } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import { NAMESPACE } from 'locale'
import { emarV2Constants } from 'locale/emarV2'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ADD_MEDICATION_STEPS } from './addMedicationSteps'

type AddMedicationStepperProps = {
  currentStepIndex: number
}

/** One value rather than stacked booleans: each marker has exactly one look. */
type StepState = 'complete' | 'current' | 'upcoming'

/** Every step before the current one is complete, every step after it upcoming. */
const getStepState = (index: number, currentStepIndex: number): StepState => {
  if (index === currentStepIndex) {
    return 'current'
  }

  return index < currentStepIndex ? 'complete' : 'upcoming'
}

/** Shows where the user is. Display only: the footer moves between steps. */
export const AddMedicationStepper = ({
  currentStepIndex
}: AddMedicationStepperProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  return (
    <StepList aria-label={t(emarV2Constants.ADD_MEDICATION_STEPS_LABEL)}>
      {ADD_MEDICATION_STEPS.map((step, index) => {
        const state = getStepState(index, currentStepIndex)
        const title = t(step.titleKey)
        const isLast = index === ADD_MEDICATION_STEPS.length - 1

        return (
          <Step
            key={step.id}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <Marker $state={state} aria-hidden>
              {state === 'complete' ? <CheckIcon size={16} /> : index + 1}
            </Marker>
            {/* The tick alone doesn't tell a screen reader the step is done */}
            <StepLabel $state={state} aria-hidden={state === 'complete'}>
              {title}
            </StepLabel>
            {state === 'complete' && (
              <VisuallyHidden>
                {t(emarV2Constants.ADD_MEDICATION_STEP_COMPLETED, {
                  step: title
                })}
              </VisuallyHidden>
            )}
            {!isLast && <Tail $isComplete={state === 'complete'} />}
          </Step>
        )
      })}
    </StepList>
  )
}

const { borderWidth, color, radius, size, spacing, text } = cssVars

/** 48px tall as designed: a two-line label plus 8px either side. */
const StepList = styled.ol`
  display: flex;
  align-items: center;
  gap: ${spacing[200]};
  margin: 0;
  padding: ${spacing[100]} 0;
  list-style: none;
`

const Step = styled.li`
  position: relative;
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: ${spacing[100]};
  min-width: 0;
`

/** One look per state; `text` colours the number and the tick alike. */
const MARKER_STYLES: Record<
  StepState,
  { background: string; border: string; text: string }
> = {
  complete: {
    background: 'transparent',
    border: color.icon.action.primary.default,
    text: color.icon.action.primary.default
  },
  current: {
    background: color.icon.action.primary.default,
    border: color.icon.action.primary.default,
    text: color.bg.primary
  },
  upcoming: {
    background: 'transparent',
    border: color.icon.action.secondary.default,
    text: color.icon.action.secondary.default
  }
}

const Marker = styled.span<{ $state: StepState }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${size[300]};
  height: ${size[300]};
  border: ${borderWidth[25]} solid
    ${({ $state }) => MARKER_STYLES[$state].border};
  border-radius: ${radius.full};
  background-color: ${({ $state }) => MARKER_STYLES[$state].background};
  color: ${({ $state }) => MARKER_STYLES[$state].text};
  font-size: ${text.body.md.size};
  line-height: 1;
`

const StepLabel = styled.span<{ $state: StepState }>`
  font-size: ${text.body.sm.size};
  line-height: ${size[200]};
  color: ${({ $state }) =>
    $state === 'upcoming'
      ? color.icon.action.secondary.default
      : color.text.secondary};
`

/** The line to the next step, blue once the step is done. */
const Tail = styled.span<{ $isComplete: boolean }>`
  flex: 1 1 0;
  min-width: ${spacing[200]};
  height: ${borderWidth[25]};
  background-color: ${({ $isComplete }) =>
    $isComplete ? color.icon.action.primary.default : color.border.subtle};
`

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`
